import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getStores, recalculateAllBalances, deleteSale, deleteExpense, apiClient } from '../api/api';
import { 
  Search, Database, DollarSign, TrendingUp, TrendingDown, 
  AlertTriangle, CheckCircle, RefreshCw, FileText, Calculator,
  Building2, ArrowRight, Eye, Trash2, X, Coins, ClipboardCheck, RotateCcw
} from 'lucide-react';

const AuditTransactions = () => {
  const { user } = useAuth();
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState('');
  const [auditData, setAuditData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null); // {type: 'sale'|'expense', id, monto, descripcion}
  const [recalculating, setRecalculating] = useState(false);
  const [recalcResult, setRecalcResult] = useState(null);
  
  // Estados para el cuadre de caja
  const [showCashCount, setShowCashCount] = useState(false);
  const [cashCountData, setCashCountData] = useState({
    billetes_200: 0,
    billetes_100: 0,
    billetes_50: 0,
    billetes_20: 0,
    billetes_10: 0,
    billetes_5: 0,
    billetes_1: 0,
    monedas_1: 0,
    monedas_050: 0,
    monedas_025: 0,
    monedas_010: 0,
    monedas_005: 0,
    otros: 0
  });
  const [cashCountNotes, setCashCountNotes] = useState('');

  useEffect(() => {
    loadStores();
  }, []);

  const loadStores = async () => {
    try {
      const data = await getStores();
      setStores(data);
    } catch (err) {
      console.error('Error loading stores:', err);
    }
  };

  const loadAuditData = async () => {
    if (!selectedStore) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiClient.get(`/admin/audit-transactions/${selectedStore}`);
      setAuditData(response.data);
    } catch (err) {
      console.error('Error loading audit data:', err);
      setError('Error al cargar datos de auditoría');
    } finally {
      setLoading(false);
    }
  };

  const handleRecalculateAll = async () => {
    setRecalculating(true);
    setRecalcResult(null);
    try {
      const result = await recalculateAllBalances();
      setRecalcResult(result);
      // Recargar datos si hay una tienda seleccionada
      if (selectedStore) {
        await loadAuditData();
      }
    } catch (err) {
      setError('Error al recalcular balances: ' + (err.response?.data?.detail || err.message));
    } finally {
      setRecalculating(false);
    }
  };

  // Función para mostrar modal de confirmación de eliminación
  const handleDeleteClick = (type, item) => {
    setItemToDelete({
      type,
      id: item.id,
      monto: item.monto,
      descripcion: type === 'sale' 
        ? `Venta de ${formatMoney(item.monto)} - ${item.metodo_pago}` 
        : `${item.tipo}: ${item.descripcion} - ${formatMoney(item.monto)}`
    });
    setShowDeleteModal(true);
  };

  // Función para confirmar eliminación
  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    
    setDeletingId(itemToDelete.id);
    try {
      if (itemToDelete.type === 'sale') {
        await deleteSale(itemToDelete.id);
      } else {
        await deleteExpense(itemToDelete.id);
      }
      
      // Recargar datos de auditoría para ver los cambios
      await loadAuditData();
      setShowDeleteModal(false);
      setItemToDelete(null);
    } catch (err) {
      alert('❌ Error al eliminar: ' + (err.response?.data?.detail || err.message));
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-GT', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatMoney = (amount) => {
    return `Q${(amount || 0).toLocaleString('es-GT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Calcular el total del conteo físico de efectivo
  const calculateCashTotal = () => {
    return (
      (cashCountData.billetes_200 * 200) +
      (cashCountData.billetes_100 * 100) +
      (cashCountData.billetes_50 * 50) +
      (cashCountData.billetes_20 * 20) +
      (cashCountData.billetes_10 * 10) +
      (cashCountData.billetes_5 * 5) +
      (cashCountData.billetes_1 * 1) +
      (cashCountData.monedas_1 * 1) +
      (cashCountData.monedas_050 * 0.50) +
      (cashCountData.monedas_025 * 0.25) +
      (cashCountData.monedas_010 * 0.10) +
      (cashCountData.monedas_005 * 0.05) +
      parseFloat(cashCountData.otros || 0)
    );
  };

  // Obtener la diferencia entre el conteo físico y el sistema
  const getCashDifference = () => {
    if (!auditData) return 0;
    const physicalCount = calculateCashTotal();
    const systemBalance = auditData.resumen.saldo_calculado || 0;
    return physicalCount - systemBalance;
  };

  // Resetear el formulario de conteo
  const resetCashCount = () => {
    setCashCountData({
      billetes_200: 0,
      billetes_100: 0,
      billetes_50: 0,
      billetes_20: 0,
      billetes_10: 0,
      billetes_5: 0,
      billetes_1: 0,
      monedas_1: 0,
      monedas_050: 0,
      monedas_025: 0,
      monedas_010: 0,
      monedas_005: 0,
      otros: 0
    });
    setCashCountNotes('');
  };

  // Manejar cambios en el conteo
  const handleCashCountChange = (field, value) => {
    const numValue = field === 'otros' ? value : parseInt(value) || 0;
    setCashCountData(prev => ({
      ...prev,
      [field]: numValue
    }));
  };

  return (
    <div className="space-y-6">
      {/* Modal de Confirmación de Eliminación Individual */}
      {showDeleteModal && itemToDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-bold text-red-600 mb-4 flex items-center">
              <Trash2 className="mr-2" size={20} />
              Confirmar Eliminación
            </h3>
            
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
              <p className="text-amber-800 text-sm">
                ¿Estás seguro de eliminar esta {itemToDelete.type === 'sale' ? 'VENTA' : 'GASTO'}?
              </p>
              <p className="text-amber-900 font-semibold mt-2">{itemToDelete.descripcion}</p>
            </div>
            
            <p className="text-gray-600 text-sm mb-4">
              💡 El saldo del dashboard se actualizará automáticamente después de eliminar.
            </p>
            
            <div className="flex space-x-3">
              <button
                onClick={handleDeleteConfirm}
                disabled={deletingId === itemToDelete.id}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 font-medium flex items-center justify-center"
              >
                {deletingId === itemToDelete.id ? (
                  <>
                    <RefreshCw className="animate-spin mr-2" size={16} />
                    Eliminando...
                  </>
                ) : (
                  <>
                    <Trash2 className="mr-2" size={16} />
                    Sí, Eliminar
                  </>
                )}
              </button>
              <button
                onClick={() => { setShowDeleteModal(false); setItemToDelete(null); }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-gradient-to-r from-slate-800 via-slate-700 to-slate-800 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center space-x-3 mb-2">
              <Database size={28} />
              <h1 className="text-2xl font-bold">Auditoría de Transacciones</h1>
            </div>
            <p className="text-slate-300">
              Verifica TODAS las transacciones de una tienda para identificar discrepancias en saldos
            </p>
          </div>
          <button
            onClick={handleRecalculateAll}
            disabled={recalculating}
            className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-500 disabled:opacity-50 flex items-center space-x-2"
          >
            <RefreshCw className={recalculating ? 'animate-spin' : ''} size={18} />
            <span>{recalculating ? 'Recalculando...' : 'Recalcular TODOS los Balances'}</span>
          </button>
        </div>
      </div>

      {/* Resultado del recálculo */}
      {recalcResult && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4">
          <div className="flex items-center space-x-2 text-green-700 mb-2">
            <CheckCircle size={20} />
            <span className="font-semibold">{recalcResult.message}</span>
          </div>
          <p className="text-green-600 text-sm">
            Tiendas procesadas: {recalcResult.total_stores} | Ajustadas: {recalcResult.stores_adjusted}
          </p>
        </div>
      )}

      {/* Selector de tienda */}
      <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
        <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
          <Building2 className="mr-2 text-blue-600" size={20} />
          Seleccionar Tienda para Auditar
        </h2>
        
        <div className="flex flex-col md:flex-row gap-4">
          <select
            value={selectedStore}
            onChange={(e) => setSelectedStore(e.target.value)}
            className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">-- Seleccione una tienda --</option>
            {stores.map(store => (
              <option key={store.id} value={store.id}>{store.name}</option>
            ))}
          </select>
          
          <button
            onClick={loadAuditData}
            disabled={!selectedStore || loading}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 font-medium"
          >
            {loading ? (
              <>
                <RefreshCw className="animate-spin" size={18} />
                <span>Cargando...</span>
              </>
            ) : (
              <>
                <Search size={18} />
                <span>Auditar Tienda</span>
              </>
            )}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <div className="flex items-center space-x-2 text-red-700">
            <AlertTriangle size={20} />
            <span>{error}</span>
          </div>
        </div>
      )}

      {auditData && (
        <>
          {/* Resumen de la Tienda */}
          <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
            <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
              <Calculator className="mr-2 text-emerald-600" size={20} />
              Resumen de {auditData.tienda.nombre}
            </h2>

            {/* Fórmula */}
            <div className="bg-slate-100 rounded-lg p-4 mb-6">
              <p className="text-sm text-slate-600 mb-2">Fórmula de cálculo:</p>
              <p className="font-mono text-slate-800">{auditData.formula}</p>
              {auditData.nota_importante && (
                <p className="text-amber-700 text-sm mt-2 font-medium">{auditData.nota_importante}</p>
              )}
            </div>

            {/* Cards de resumen */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
              {/* Ventas que entran a CAJA */}
              <div className="bg-emerald-50 rounded-lg p-4 border-2 border-emerald-300">
                <p className="text-xs text-emerald-600 font-medium">💵 Ventas a CAJA</p>
                <p className="text-xl font-bold text-emerald-700">{formatMoney(auditData.resumen.ventas_efectivo || 0)}</p>
                <p className="text-xs text-emerald-500">{auditData.resumen.cantidad_ventas_efectivo || 0} transacciones</p>
                <p className="text-xs text-emerald-600 font-semibold mt-1">Efectivo+Recaudo+Recargas+Sobrantes+Transferencia</p>
              </div>
              
              {/* Ventas POS - NO ENTRAN A CAJA */}
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200 opacity-75">
                <p className="text-xs text-blue-600 font-medium">🏦 Ventas POS</p>
                <p className="text-xl font-bold text-blue-700">{formatMoney(auditData.resumen.ventas_otros || 0)}</p>
                <p className="text-xs text-blue-500">{(auditData.resumen.cantidad_ventas || 0) - (auditData.resumen.cantidad_ventas_efectivo || 0)} transacciones</p>
                <p className="text-xs text-blue-600">Va al banco (no caja)</p>
              </div>
              
              <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                <p className="text-xs text-red-600 font-medium">Gastos Efectivo</p>
                <p className="text-xl font-bold text-red-700">{formatMoney(auditData.resumen.gastos_efectivo)}</p>
                <p className="text-xs text-red-500">{auditData.resumen.cantidad_gastos_efectivo} transacciones</p>
              </div>
              
              <div className="bg-purple-50 rounded-lg p-4 border border-purple-200 opacity-75">
                <p className="text-xs text-purple-600 font-medium">Gastos Transferencia</p>
                <p className="text-xl font-bold text-purple-700">{formatMoney(auditData.resumen.gastos_transferencia)}</p>
                <p className="text-xs text-purple-500">{auditData.resumen.cantidad_gastos_transferencia} (NO afecta caja)</p>
              </div>
              
              <div className={`rounded-lg p-4 border-2 ${auditData.resumen.saldo_sin_inicial >= 0 ? 'bg-emerald-50 border-emerald-300' : 'bg-red-50 border-red-300'}`}>
                <p className={`text-xs font-medium ${auditData.resumen.saldo_sin_inicial >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>💰 SALDO EN CAJA</p>
                <p className={`text-xl font-bold ${auditData.resumen.saldo_sin_inicial >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                  {formatMoney(auditData.resumen.saldo_sin_inicial)}
                </p>
                <p className="text-xs text-gray-500">Efectivo - Gastos</p>
              </div>
            </div>

            {/* Verificación de consistencia */}
            <div className={`rounded-lg p-4 border-2 ${Math.abs(auditData.resumen.diferencia_con_bd) < 1 ? 'bg-emerald-50 border-emerald-300' : 'bg-red-50 border-red-300'}`}>
              <div className="flex items-center space-x-3">
                {Math.abs(auditData.resumen.diferencia_con_bd) < 1 ? (
                  <CheckCircle className="text-emerald-600" size={24} />
                ) : (
                  <AlertTriangle className="text-red-600" size={24} />
                )}
                <div>
                  <p className={`font-semibold ${Math.abs(auditData.resumen.diferencia_con_bd) < 1 ? 'text-emerald-700' : 'text-red-700'}`}>
                    {Math.abs(auditData.resumen.diferencia_con_bd) < 1 
                      ? '✅ Los saldos coinciden correctamente' 
                      : `⚠️ DIFERENCIA DETECTADA: ${formatMoney(auditData.resumen.diferencia_con_bd)}`}
                  </p>
                  <p className="text-sm text-gray-600">
                    Saldo en BD: {formatMoney(auditData.tienda.saldo_en_bd)} | 
                    Saldo Calculado: {formatMoney(auditData.resumen.saldo_calculado)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* ============== SECCIÓN DE CUADRE DE CAJA ============== */}
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
            <div 
              className="bg-gradient-to-r from-amber-500 to-orange-500 p-4 cursor-pointer flex items-center justify-between"
              onClick={() => setShowCashCount(!showCashCount)}
            >
              <div className="flex items-center space-x-3 text-white">
                <Coins size={24} />
                <div>
                  <h2 className="text-lg font-bold">Cuadre de Caja - Conteo Físico</h2>
                  <p className="text-amber-100 text-sm">Ingrese las cantidades para cuadrar el cierre</p>
                </div>
              </div>
              <button className="text-white bg-white/20 rounded-lg px-4 py-2 hover:bg-white/30 transition-colors">
                {showCashCount ? '▲ Ocultar' : '▼ Mostrar Conteo'}
              </button>
            </div>

            {showCashCount && (
              <div className="p-6">
                {/* Grid de Denominaciones */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  {/* Billetes */}
                  <div>
                    <h3 className="font-bold text-gray-700 mb-3 flex items-center">
                      <span className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-2 text-green-600">💵</span>
                      Billetes
                    </h3>
                    <div className="space-y-2">
                      {[
                        { key: 'billetes_200', label: 'Q200', value: 200 },
                        { key: 'billetes_100', label: 'Q100', value: 100 },
                        { key: 'billetes_50', label: 'Q50', value: 50 },
                        { key: 'billetes_20', label: 'Q20', value: 20 },
                        { key: 'billetes_10', label: 'Q10', value: 10 },
                        { key: 'billetes_5', label: 'Q5', value: 5 },
                        { key: 'billetes_1', label: 'Q1', value: 1 },
                      ].map(denom => (
                        <div key={denom.key} className="flex items-center space-x-3 bg-gray-50 rounded-lg p-2">
                          <span className="w-16 font-semibold text-gray-700">{denom.label}</span>
                          <span className="text-gray-400">×</span>
                          <input
                            type="number"
                            min="0"
                            value={cashCountData[denom.key]}
                            onChange={(e) => handleCashCountChange(denom.key, e.target.value)}
                            className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-center focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                          />
                          <span className="text-gray-400">=</span>
                          <span className="w-24 text-right font-medium text-emerald-600">
                            {formatMoney(cashCountData[denom.key] * denom.value)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Monedas */}
                  <div>
                    <h3 className="font-bold text-gray-700 mb-3 flex items-center">
                      <span className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center mr-2 text-amber-600">🪙</span>
                      Monedas
                    </h3>
                    <div className="space-y-2">
                      {[
                        { key: 'monedas_1', label: 'Q1.00', value: 1 },
                        { key: 'monedas_050', label: 'Q0.50', value: 0.50 },
                        { key: 'monedas_025', label: 'Q0.25', value: 0.25 },
                        { key: 'monedas_010', label: 'Q0.10', value: 0.10 },
                        { key: 'monedas_005', label: 'Q0.05', value: 0.05 },
                      ].map(denom => (
                        <div key={denom.key} className="flex items-center space-x-3 bg-gray-50 rounded-lg p-2">
                          <span className="w-16 font-semibold text-gray-700">{denom.label}</span>
                          <span className="text-gray-400">×</span>
                          <input
                            type="number"
                            min="0"
                            value={cashCountData[denom.key]}
                            onChange={(e) => handleCashCountChange(denom.key, e.target.value)}
                            className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-center focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                          />
                          <span className="text-gray-400">=</span>
                          <span className="w-24 text-right font-medium text-emerald-600">
                            {formatMoney(cashCountData[denom.key] * denom.value)}
                          </span>
                        </div>
                      ))}
                    </div>

                    {/* Otros (vales, cheques, etc.) */}
                    <div className="mt-4">
                      <h3 className="font-bold text-gray-700 mb-3 flex items-center">
                        <span className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-2 text-blue-600">📝</span>
                        Otros (vales, cheques, etc.)
                      </h3>
                      <div className="flex items-center space-x-3 bg-gray-50 rounded-lg p-2">
                        <span className="w-16 font-semibold text-gray-700">Monto:</span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={cashCountData.otros}
                          onChange={(e) => handleCashCountChange('otros', e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Notas del cuadre */}
                <div className="mb-6">
                  <label className="block font-bold text-gray-700 mb-2">
                    📋 Notas del Cuadre (opcional)
                  </label>
                  <textarea
                    value={cashCountNotes}
                    onChange={(e) => setCashCountNotes(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                    rows={2}
                    placeholder="Ej: Faltante justificado por devolución de cliente..."
                  />
                </div>

                {/* Resumen del Cuadre */}
                <div className="bg-slate-100 rounded-xl p-6">
                  <h3 className="font-bold text-gray-800 mb-4 flex items-center">
                    <ClipboardCheck className="mr-2 text-slate-600" size={20} />
                    Resultado del Cuadre
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Total Contado */}
                    <div className="bg-white rounded-lg p-4 border-2 border-amber-300">
                      <p className="text-sm text-amber-600 font-medium">💵 Efectivo Contado</p>
                      <p className="text-2xl font-bold text-amber-700">{formatMoney(calculateCashTotal())}</p>
                      <p className="text-xs text-gray-500">Suma de billetes + monedas + otros</p>
                    </div>
                    
                    {/* Saldo Sistema */}
                    <div className="bg-white rounded-lg p-4 border-2 border-blue-300">
                      <p className="text-sm text-blue-600 font-medium">🖥️ Saldo del Sistema</p>
                      <p className="text-2xl font-bold text-blue-700">{formatMoney(auditData?.resumen?.saldo_calculado || 0)}</p>
                      <p className="text-xs text-gray-500">Según transacciones registradas</p>
                    </div>
                    
                    {/* Diferencia */}
                    <div className={`bg-white rounded-lg p-4 border-2 ${
                      Math.abs(getCashDifference()) < 1 
                        ? 'border-emerald-300' 
                        : getCashDifference() > 0 
                          ? 'border-emerald-300' 
                          : 'border-red-300'
                    }`}>
                      <p className={`text-sm font-medium ${
                        Math.abs(getCashDifference()) < 1 
                          ? 'text-emerald-600' 
                          : getCashDifference() > 0 
                            ? 'text-emerald-600' 
                            : 'text-red-600'
                      }`}>
                        {Math.abs(getCashDifference()) < 1 
                          ? '✅ Diferencia' 
                          : getCashDifference() > 0 
                            ? '📈 Sobrante' 
                            : '📉 Faltante'}
                      </p>
                      <p className={`text-2xl font-bold ${
                        Math.abs(getCashDifference()) < 1 
                          ? 'text-emerald-700' 
                          : getCashDifference() > 0 
                            ? 'text-emerald-700' 
                            : 'text-red-700'
                      }`}>
                        {formatMoney(Math.abs(getCashDifference()))}
                      </p>
                      <p className="text-xs text-gray-500">
                        {Math.abs(getCashDifference()) < 1 
                          ? '¡Cuadre perfecto!' 
                          : getCashDifference() > 0 
                            ? 'Hay más efectivo del esperado' 
                            : 'Hay menos efectivo del esperado'}
                      </p>
                    </div>
                  </div>

                  {/* Alerta de diferencia significativa */}
                  {Math.abs(getCashDifference()) >= 1 && (
                    <div className={`mt-4 rounded-lg p-4 ${getCashDifference() > 0 ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`}>
                      <div className="flex items-start space-x-3">
                        <AlertTriangle className={getCashDifference() > 0 ? 'text-emerald-600' : 'text-red-600'} size={20} />
                        <div>
                          <p className={`font-semibold ${getCashDifference() > 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                            {getCashDifference() > 0 
                              ? `Se encontró un SOBRANTE de ${formatMoney(getCashDifference())}`
                              : `Se detectó un FALTANTE de ${formatMoney(Math.abs(getCashDifference()))}`}
                          </p>
                          <p className="text-sm text-gray-600 mt-1">
                            {getCashDifference() > 0 
                              ? 'Revisar si hay ventas no registradas o devoluciones pendientes.'
                              : 'Revisar si hay gastos no registrados, devoluciones o errores en el cambio.'}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Botón para limpiar */}
                  <div className="mt-4 flex justify-end">
                    <button
                      onClick={resetCashCount}
                      className="px-4 py-2 text-gray-600 bg-gray-200 rounded-lg hover:bg-gray-300 flex items-center space-x-2"
                    >
                      <RotateCcw size={16} />
                      <span>Limpiar Conteo</span>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Lista de Ventas */}
          <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
            <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
              <TrendingUp className="mr-2 text-emerald-600" size={20} />
              Ventas Registradas ({auditData.ventas.length})
            </h2>
            
            {auditData.ventas.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No hay ventas registradas</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-emerald-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-emerald-700">#</th>
                      <th className="px-4 py-2 text-left text-emerald-700">Fecha</th>
                      <th className="px-4 py-2 text-right text-emerald-700">Monto</th>
                      <th className="px-4 py-2 text-left text-emerald-700">Método</th>
                      <th className="px-4 py-2 text-center text-emerald-700">¿Entra a Caja?</th>
                      <th className="px-4 py-2 text-left text-emerald-700">Vendedor</th>
                      <th className="px-4 py-2 text-center text-emerald-700">Acción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditData.ventas.map((venta, idx) => (
                      <tr key={venta.id} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} ${!venta.afecta_caja ? 'opacity-60' : ''} hover:bg-emerald-50 transition-colors`}>
                        <td className="px-4 py-2 text-gray-600">{idx + 1}</td>
                        <td className="px-4 py-2">{formatDate(venta.fecha)}</td>
                        <td className="px-4 py-2 text-right font-medium text-emerald-600">{formatMoney(venta.monto)}</td>
                        <td className="px-4 py-2">
                          <span className={`px-2 py-1 rounded text-xs ${venta.afecta_caja ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}`}>
                            {venta.metodo_pago}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-center">
                          {venta.afecta_caja ? (
                            <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded text-xs font-semibold">SÍ 💵</span>
                          ) : (
                            <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">NO 🏦</span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-gray-600">{venta.vendedor}</td>
                        <td className="px-4 py-2 text-gray-400 text-xs font-mono">{venta.id?.slice(0, 8)}...</td>
                        <td className="px-4 py-2 text-center">
                          <button
                            onClick={() => handleDeleteClick('sale', venta)}
                            disabled={deletingId === venta.id}
                            className="p-1.5 text-red-600 hover:bg-red-100 rounded-lg transition-colors disabled:opacity-50"
                            title="Eliminar esta venta"
                          >
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-emerald-100">
                    <tr>
                      <td colSpan="2" className="px-4 py-2 font-bold text-emerald-800">TOTAL VENTAS EFECTIVO</td>
                      <td className="px-4 py-2 text-right font-bold text-emerald-800">
                        {formatMoney(auditData.resumen.ventas_efectivo || 0)}
                      </td>
                      <td colSpan="4" className="px-4 py-2 text-gray-500 text-sm">
                        (Total todas: {formatMoney(auditData.resumen.total_ventas)} | POS/Otros: {formatMoney(auditData.resumen.ventas_otros || 0)})
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>

          {/* Lista de Gastos */}
          <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
            <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
              <TrendingDown className="mr-2 text-red-600" size={20} />
              Gastos Registrados ({auditData.gastos.length})
            </h2>
            
            {auditData.gastos.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No hay gastos registrados</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-red-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-red-700">#</th>
                      <th className="px-4 py-2 text-left text-red-700">Fecha</th>
                      <th className="px-4 py-2 text-right text-red-700">Monto</th>
                      <th className="px-4 py-2 text-left text-red-700">Tipo</th>
                      <th className="px-4 py-2 text-left text-red-700">Descripción</th>
                      <th className="px-4 py-2 text-center text-red-700">¿Afecta Caja?</th>
                      <th className="px-4 py-2 text-center text-red-700">Acción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditData.gastos.map((gasto, idx) => (
                      <tr key={gasto.id} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} ${!gasto.afecta_caja ? 'opacity-60' : ''} hover:bg-red-50 transition-colors`}>
                        <td className="px-4 py-2 text-gray-600">{idx + 1}</td>
                        <td className="px-4 py-2">{formatDate(gasto.fecha)}</td>
                        <td className="px-4 py-2 text-right font-medium text-red-600">{formatMoney(gasto.monto)}</td>
                        <td className="px-4 py-2">
                          <span className={`px-2 py-1 rounded text-xs ${gasto.afecta_caja ? 'bg-red-100 text-red-700' : 'bg-purple-100 text-purple-700'}`}>
                            {gasto.tipo}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-gray-600 max-w-xs truncate">{gasto.descripcion}</td>
                        <td className="px-4 py-2 text-center">
                          {gasto.afecta_caja ? (
                            <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs">SÍ (Efectivo)</span>
                          ) : (
                            <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs">NO (Transferencia)</span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-center">
                          <button
                            onClick={() => handleDeleteClick('expense', gasto)}
                            disabled={deletingId === gasto.id}
                            className="p-1.5 text-red-600 hover:bg-red-100 rounded-lg transition-colors disabled:opacity-50"
                            title="Eliminar este gasto"
                          >
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-red-100">
                    <tr>
                      <td colSpan="2" className="px-4 py-2 font-bold text-red-800">TOTAL GASTOS EFECTIVO</td>
                      <td className="px-4 py-2 text-right font-bold text-red-800">
                        {formatMoney(auditData.resumen.gastos_efectivo)}
                      </td>
                      <td colSpan="4" className="px-4 py-2 text-gray-500 text-sm">
                        (Transferencias: {formatMoney(auditData.resumen.gastos_transferencia)} - no afectan caja)
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AuditTransactions;
